import { FC, useMemo } from 'react';
import { CtaFeatureSlice } from '@/types';
import { Container } from '@/components';
import classNames from 'classnames';
import { RenderCta } from './CtaFeature.components';
import { getSlicePadding, isCurrentDateInRange, toZonedDate } from '@/utils';
import { useSelectedStore } from '@/hooks';
import { format } from 'date-fns';

import styles from './CtaFeature.module.scss';

const CtaFeature: FC<CtaFeatureSlice> = slice => {
  const { store: currentStore } = useSelectedStore();
  const background = slice?.primary?.background_color ?? '';
  const borderTop = slice?.primary?.border_section ?? '';
  const borderBottom = slice?.primary?.border_section ?? '';
  const paddingSection = slice?.primary?.padding_section;
  const fullWidth =
    slice?.variation === 'default' ? slice?.primary?.cta_full_width : false;

  const currentStoreUrl = currentStore?.url_slug ?? '';

  const imageTwoCTAalignLeft =
    slice.variation === 'imageWithTwoCta' &&
    slice?.primary?.content_align === 'left';

  const shouldHideSlice = useMemo(() => {
    if (slice?.variation === 'default') {
      const items = slice?.items?.filter(item =>
        isItemVisible(item, currentStoreUrl),
      );

      return !items?.length;
    }

    return !isItemVisible(slice.primary, currentStoreUrl);
  }, [slice, currentStore]);

  if (shouldHideSlice || !slice?.primary?.active) {
    return null;
  }

  return (
    <section
      className={classNames(
        styles.cta_section,
        {
          [styles.cta_section__spacing_bottom]: slice?.primary?.bottom_spacing,
        },
        getSlicePadding(paddingSection),
      )}
      style={{ background, borderTop, borderBottom }}
    >
      <Container
        className={classNames({
          [styles.full_width_container]:
            fullWidth || slice.variation === 'imageWithLogoAndButton',
          [styles.centered_button_variation_container]:
            slice.variation === 'centeredButton',
          [styles.default_variation_container]: slice.variation === 'default',
          [styles.image_two_cta_variation_container]:
            slice.variation === 'imageWithTwoCta',
          [styles.image_two_cta_variation_container__align_left]:
            imageTwoCTAalignLeft,
          [styles.title_cta_variation_container]:
            slice.variation === 'titleWithCta',
          [styles.title_two_link_variation_container]:
            slice.variation === 'titleWithTwoLinks',
          [styles.image_title_button_variation_container]:
            slice.variation === 'imageWithTitleAndButton',
          [styles.image_logo_button_variation_container]:
            slice.variation === 'imageWithLogoAndButton',
        })}
      >
        <RenderCta {...slice} />
      </Container>
    </section>
  );
};

const isItemVisible = (
  data: {
    location_slug?: string | null;
    start_date: string | null;
    end_date: string | null;
    day: string | null;
  },
  currentStoreUrl?: string,
): boolean => {
  const { location_slug: slugs, start_date: start, end_date: end, day } = data;

  const currentDay = format(toZonedDate(), 'eeee');
  const isDayValid = [null, 'Everyday', currentDay].includes(day);
  const isDateInRange = isCurrentDateInRange(start, end);

  const parsedSlugs = slugs?.split(',')?.map(slug => slug.trim()) || [];
  const validLocations = [null, currentStoreUrl];
  const isLocationValid =
    !parsedSlugs?.length ||
    parsedSlugs.some(slug => validLocations.includes(slug));

  return isDayValid && isDateInRange && isLocationValid;
};

export default CtaFeature;
